// packages/app-shell/src/process/flow-graph.ts
//
// Pure, React-free flow-graph model for the process swimlane. The graph is the
// single model for the flow chart: typed nodes — steps (`s<stepIndex>`),
// non-step flow nodes (`r<rowIdx>`: terminators / subprocess headers) and
// gateway diamonds (`split:<frame>` / `merge:<frame>`) — plus directed edges in
// execution order. Each rendered arrow is tagged with its edge id; the
// step-travel droplet BFS-walks this graph from the previous active step to the
// current one. Poly-path geometry helpers re-export from
// @archlang/scene/process (shared with the headless SVG renderer), so all of
// it is unit-testable without React/DOM.

import type { Row, CompensationFrame, Reentry } from "@archlang/engine";
import { polyPathLength } from "@archlang/scene/process";

/** Temporal chain of flow nodes (steps + terminators + subprocess headers),
 *  each linked to its execution predecessor. Branch-aware. */
export interface FlowChain {
  readonly nodes: readonly { readonly row: Row; readonly idx: number }[];
  readonly prevOf: ReadonlyMap<number, Row>;
}

export interface GwBranch {
  readonly label: string;
  /** Branch entry/exit node — a flow node, or a nested gateway's split (head) /
   *  merge (tail) diamond. `buildFlowGraph` keys it via `nodeKey`, so a nested
   *  gateway head/tail wires diamond→diamond. */
  readonly head: Row;
  readonly tail: Row;
}

/** Fork/continuation anchor: a flow-node block (`x` null) or another frame's
 *  gateway diamond (`x` set). */
export interface GwAnchor {
  readonly row: Row;
  readonly x: number | null;
}

/** One branched frame, resolved to its split/merge rows, branch heads/tails,
 *  and the fork/continuation anchors that bracket it. */
export interface Gw {
  readonly frame: { readonly id: string; readonly kind: string };
  readonly split: Row;
  readonly merge: Row;
  readonly branches: readonly GwBranch[];
  readonly fork: GwAnchor | null;
  readonly continuation: GwAnchor | null;
  /** `parallel out` — DETACHED (fire-and-forget). The branches don't rejoin: no
   *  tail→merge, and the main flow BYPASSES the block (fork → continuation) so it
   *  continues uninterrupted while the branches fan out. */
  readonly detached?: boolean;
}

export interface GatewayLegIds {
  fork?: string;
  heads: Map<string, string>;
  tails: Map<string, string>;
  cont?: string;
}

/** A non-forward edge layered over the flow graph — drawn + routed, but NEVER
 *  part of `adj` (so the forward step-travel BFS can't traverse it):
 *   - `drop` — a saga forward step's failure entry into the compensation chain;
 *   - `comp` — a link of the reverse compensation chain (… → fail);
 *   - `reentry` — a `go` back-arc to its `as` target (PROCESS.md §7).
 *  Endpoints are flow-graph node keys (`s<idx>` / `r<idx>`). */
export type AuxEdgeKind = "drop" | "comp" | "reentry";
export interface AuxEdge {
  readonly id: string;
  readonly from: string;
  readonly to: string;
  readonly kind: AuxEdgeKind;
  /** Freehand comment authored on a `go` step (`go name "note"`) — rendered as
   *  the re-entry arc's midpoint label. Only ever set on a `reentry` aux edge. */
  readonly comment?: string;
}

export interface FlowGraph {
  /** Forward adjacency: node key → outgoing edges. */
  readonly adj: Map<string, { id: string; to: string }[]>;
  /** Back/compensation/re-entry edges — rendered + routed, NOT in `adj`. */
  readonly auxEdges: readonly AuxEdge[];
  /** Step index → its node key (only real steps are droplet targets). */
  readonly stepToKey: Map<number, string>;
  /** flowChain node idx → the edge id of its incoming step→step arrow. */
  readonly flowEdgeIdByNodeIdx: Map<number, string>;
  /** Gateway frame id → its leg edge ids (fork / per-branch heads+tails / cont). */
  readonly gatewayEdgeIds: Map<string, GatewayLegIds>;
  /** Edge id → its endpoints (+ the called step's freehand comment, when the
   *  forward edge lands on a commented call — its midpoint label). */
  readonly edgeInfo: Map<string, { from: string; to: string; comment?: string }>;
  /** Split node keys belonging to `parallel` frames (droplet fans here). */
  readonly parallelSplits: Set<string>;
}

/**
 * Build the flow graph from the temporal chain + the resolved gateways. Pure:
 * no geometry, no DOM. Edge ids are `${fromKey}__${toKey}`.
 *
 *  - Flow step→step edges: every chain node not handled by a gateway.
 *  - Gateway edges: fork→split, split→each head, each (non-terminating)
 *    tail→merge, merge→continuation. A gateway fork/continuation that targets
 *    another frame's diamond yields a gateway→gateway edge (`merge:…`/`split:…`).
 */
export function buildFlowGraph(
  flowChain: FlowChain,
  gateways: readonly Gw[],
  gatewaySkip: ReadonlySet<Row>,
  /** Collapsed `do` ranges (walk.collapsedGroups). A collapsed subprocess hides
   *  its body, so every body step is mapped to the `do` header node — the
   *  droplet then travels to/from the collapsed `do` rather than a missing
   *  block. Omit / empty when nothing is collapsed. */
  collapsedGroups?: ReadonlyMap<string, { startIndex: number; endIndex: number }>,
  /** Saga compensation structure + `go` re-entries + a nodeId→nodeKey resolver,
   *  turned into `auxEdges`. Omit for non-saga / non-`go` processes. */
  back?: {
    readonly compensation?: readonly CompensationFrame[];
    readonly reentries?: readonly Reentry[];
    /** Walker nodeId → flow-graph node key (`s<idx>` step / `r<idx>` terminator).
     *  Built by the caller from `walkResult.steps` + `layout.rows`. */
    readonly nodeKeyByNodeId?: ReadonlyMap<string, string>;
  },
): FlowGraph {
  const adj = new Map<string, { id: string; to: string }[]>();
  const stepToKey = new Map<number, string>();
  const flowEdgeIdByNodeIdx = new Map<number, string>();
  const edgeInfo = new Map<string, { from: string; to: string; comment?: string }>();
  const parallelSplits = new Set<string>();
  const gatewayEdgeIds = new Map<string, GatewayLegIds>();

  const idxOfRow = new Map<Row, number>();
  // Step-row freehand comment (`> Callee "note"` / `go name "note"`), keyed by
  // node key — the forward edge picks up the CALLED step's comment; the `go`
  // re-entry aux edge picks up the go step's comment.
  const commentByKey = new Map<string, string>();
  flowChain.nodes.forEach(({ row, idx }) => {
    idxOfRow.set(row, idx);
    if (row.stepIndex != null) {
      stepToKey.set(row.stepIndex, `s${row.stepIndex}`);
      if (row.comment) commentByKey.set(`s${row.stepIndex}`, row.comment);
    }
  });
  const rowKey = (row: Row) => (row.stepIndex != null ? `s${row.stepIndex}` : `r${idxOfRow.get(row) ?? -1}`);
  // A node's graph key. A gateway row resolves to its diamond node
  // (`split:<fid>` / `merge:<fid>`), so a branch whose head/tail IS a nested
  // gateway wires split→nested-split / nested-merge→merge — gateways are nodes
  // in the graph, treated uniformly with steps.
  const nodeKey = (row: Row) =>
    row.gateway ? `${row.gateway.kind}:${row.gateway.frameId}` : rowKey(row);
  // A collapsed `do`'s body steps resolve to its header node, so a transition
  // into/out of the collapsed subprocess animates to/from the `do` block.
  if (collapsedGroups) {
    for (const { row } of flowChain.nodes) {
      const sh = row.subprocessHeader;
      if (!sh || sh.expanded) continue;
      const range = collapsedGroups.get(sh.nodeId);
      if (!range) continue;
      const key = rowKey(row);
      for (let s = range.startIndex; s <= range.endIndex; s++) stepToKey.set(s, key);
    }
  }
  const addEdge = (from: string, to: string, comment?: string): string => {
    const id = `${from}__${to}`;
    let a = adj.get(from);
    if (!a) adj.set(from, (a = []));
    a.push({ id, to });
    edgeInfo.set(id, comment ? { from, to, comment } : { from, to });
    return id;
  };

  for (const { row, idx } of flowChain.nodes) {
    if (gatewaySkip.has(row)) continue;
    const prev = flowChain.prevOf.get(idx);
    if (!prev) continue;
    // The forward arrow into a commented call carries that call's comment as its
    // midpoint label.
    flowEdgeIdByNodeIdx.set(idx, addEdge(rowKey(prev), rowKey(row), row.comment));
  }

  for (const g of gateways) {
    const sK = `split:${g.frame.id}`, mK = `merge:${g.frame.id}`;
    if (g.frame.kind === "parallel") parallelSplits.add(sK);
    const ge: GatewayLegIds = { fork: undefined, heads: new Map(), tails: new Map(), cont: undefined };
    if (g.fork) {
      // A gateway-anchored fork (x set) attaches to that diamond — usually a
      // prior SIBLING's merge, but for a gateway that OPENS a parent branch it
      // is the ENCLOSING frame's split. Derive the node key from the anchor
      // row's gateway kind rather than assuming "merge".
      const fromK = g.fork.x === null
        ? rowKey(g.fork.row)
        : `${g.fork.row.gateway?.kind ?? "merge"}:${g.fork.row.gateway?.frameId ?? "?"}`;
      ge.fork = addEdge(fromK, sK);
    }
    // split → head and tail → merge. head/tail may be a nested gateway, in
    // which case nodeKey wires diamond→diamond (split→nested-split,
    // nested-merge→merge); the nested gateway's own fork/cont is null at that
    // boundary, so there's no double edge.
    for (const b of g.branches) ge.heads.set(b.label, addEdge(sK, nodeKey(b.head)));
    for (const b of g.branches) {
      // A DETACHED `parallel out` never rejoins — its branches fan out and stop,
      // exactly like a fail/finish dead-end (no tail → merge).
      if (b.tail.terminator || g.detached) continue;
      ge.tails.set(b.label, addEdge(nodeKey(b.tail), mK));
    }
    if (g.continuation) {
      // Symmetric to fork: usually the next sibling's SPLIT, but a gateway that
      // CLOSES a parent branch continues into the enclosing frame's merge.
      const toK = g.continuation.x === null
        ? rowKey(g.continuation.row)
        : `${g.continuation.row.gateway?.kind ?? "split"}:${g.continuation.row.gateway?.frameId ?? "?"}`;
      // Detached (`parallel out`): the main flow BYPASSES the block, continuing
      // from the FORK (the step before the parallel), not from a merge that never
      // happens. A detached block that is the FIRST action has no fork — nothing
      // upstream to continue from — so draw no continuation (the next node is a
      // root); never fall back to a `merge:<frame>` diamond a detached frame omits.
      const forkK = g.fork
        ? (g.fork.x === null
            ? rowKey(g.fork.row)
            : `${g.fork.row.gateway?.kind ?? "merge"}:${g.fork.row.gateway?.frameId ?? "?"}`)
        : null;
      const fromK = g.detached ? forkK : mK;
      if (fromK) ge.cont = addEdge(fromK, toK);
    }
    gatewayEdgeIds.set(g.frame.id, ge);
  }

  // Aux edges (drop / comp / reentry) — resolved to node keys, kept OUT of `adj`
  // so the forward droplet BFS never walks them. Endpoints that don't resolve
  // (a `go` whose `as` target is undefined, a hidden row) are dropped.
  const auxEdges: AuxEdge[] = [];
  const key = back?.nodeKeyByNodeId;
  const addAux = (fromId: string | undefined, toId: string | undefined, kind: AuxEdgeKind): void => {
    const from = fromId && key?.get(fromId);
    const to = toId && key?.get(toId);
    if (!from || !to || from === to) return;
    // A `go "note"` labels its re-entry arc; drop/comp arcs carry no comment.
    const comment = kind === "reentry" ? commentByKey.get(from) : undefined;
    auxEdges.push({ id: `aux:${kind}:${from}__${to}`, from, to, kind, ...(comment ? { comment } : {}) });
  };
  for (const f of back?.compensation ?? []) {
    for (let i = 0; i + 1 < f.chain.length; i++) addAux(f.chain[i], f.chain[i + 1], "comp");
    for (const d of f.drops) addAux(d.fromNodeId, d.toNodeId, "drop");
  }
  for (const re of back?.reentries ?? []) addAux(re.fromNodeId, re.toNodeId, "reentry");

  return { adj, auxEdges, stepToKey, flowEdgeIdByNodeIdx, gatewayEdgeIds, edgeInfo, parallelSplits };
}

/** A graph with no nodes — used when the swimlane isn't in flow variant. */
export function emptyFlowGraph(): FlowGraph {
  return buildFlowGraph({ nodes: [], prevOf: new Map() }, [], new Set());
}

/** Whether `CompensationFrame` / `Reentry` exist to be drawn — lets the caller
 *  skip building the nodeId→nodeKey map when there's nothing back-edged. */
export function hasBackEdges(
  compensation: readonly CompensationFrame[] | undefined,
  reentries: readonly Reentry[] | undefined,
): boolean {
  return (compensation?.length ?? 0) > 0 || (reentries?.some((r) => r.toNodeId) ?? false);
}

/**
 * Shortest forward edge-id path through the graph from `fromKey` to `toKey`
 * (BFS). When there's no `fromKey` (first step) or no forward path, fall back
 * to the single edge incoming to `toKey`.
 */
export function bfsEdgePath(
  adj: ReadonlyMap<string, { id: string; to: string }[]>,
  fromKey: string | null,
  toKey: string | null,
): string[] {
  if (!toKey) return [];
  const incoming = (): string[] => {
    for (const [, es] of adj) for (const e of es) if (e.to === toKey) return [e.id];
    return [];
  };
  if (!fromKey || fromKey === toKey) return incoming();
  const seen = new Set<string>([fromKey]);
  const q: { node: string; path: string[] }[] = [{ node: fromKey, path: [] }];
  while (q.length) {
    const { node, path } = q.shift()!;
    for (const e of adj.get(node) ?? []) {
      const np = [...path, e.id];
      if (e.to === toKey) return np;
      if (!seen.has(e.to)) { seen.add(e.to); q.push({ node: e.to, path: np }); }
    }
  }
  return incoming();
}

/**
 * Whether a step transition should ANIMATE the droplet, or apply instantly.
 * Only a FORWARD scrubber / auto-play change in the flow variant animates —
 * first render, no-op changes, a manual in-diagram click, the sequence variant,
 * or scrubbing BACKWARD all apply instantly (highlight immediately). Backward
 * has no forward path along the arrows, so animating it just flickers the
 * target's incoming edge — instant reads cleaner.
 */
export function shouldAnimateTransition(
  prev: number | null | undefined,
  next: number | null,
  manual: boolean,
  isFlow: boolean,
): boolean {
  if (prev === undefined || prev === next) return false;
  if (manual || !isFlow) return false;
  if (prev != null && next != null && next < prev) return false; // backward → instant
  return true;
}

export interface DropletLeg {
  readonly id: string;
  readonly d: string;
  readonly delaySec: number;
}

/** Visual duration bounds for one droplet leg — a very short hop still reads,
 *  a very long one doesn't drag. The RENDERER and the leg SEQUENCING must use
 *  the same value, or legs overlap (short) / leave gaps (long). */
export const DROPLET_MIN_DUR = 0.18;
export const DROPLET_MAX_DUR = 2.4;
export function legDuration(d: string, speed: number): number {
  return Math.max(DROPLET_MIN_DUR, Math.min(polyPathLength(d) / speed, DROPLET_MAX_DUR));
}

/**
 * Build the sequenced droplet legs for an edge path. Each leg begins when the
 * previous finishes (`delaySec` = cumulative length ÷ speed), so the ball reads
 * as one continuous traversal. At a PARALLEL split every sibling head fans
 * concurrently (shares the split leg's delay). Returns the legs plus the total
 * duration — when the ball reaches the destination (used to delay the target's
 * highlight). `edgeD` resolves an edge id to its measured path; legs with no
 * geometry are skipped. Pure: no DOM, no React.
 */
export function dropletChain(
  graph: Pick<FlowGraph, "adj" | "edgeInfo" | "parallelSplits">,
  edgeIds: readonly string[],
  edgeD: (id: string) => string | null | undefined,
  speed: number,
): { legs: DropletLeg[]; totalDur: number } {
  let delay = 0;
  const legs: DropletLeg[] = [];
  for (const id of edgeIds) {
    const d = edgeD(id);
    if (!d) continue;
    const legDur = legDuration(d, speed); // SAME clamp the renderer applies
    legs.push({ id, d, delaySec: delay });
    const info = graph.edgeInfo.get(id);
    if (info && graph.parallelSplits.has(info.from)) {
      for (const sib of graph.adj.get(info.from) ?? []) {
        if (sib.id === id) continue;
        const sd = edgeD(sib.id);
        if (sd) legs.push({ id: sib.id, d: sd, delaySec: delay });
      }
    }
    delay += legDur;
  }
  // Arrival = when the CHOSEN path's ball reaches the destination = the
  // accumulated delay of the main legs. Concurrent parallel-fan siblings go to
  // OTHER branches, so they must NOT inflate this (the destination highlight
  // would otherwise wait on an unrelated branch).
  return { legs, totalDur: delay };
}

// Poly-path helpers (`polyPathLength` / `polyPathPointAt` / `roundedPolyPath`)
// come from @archlang/scene/process — the same 2dp-rounded implementations the
// headless SVG export draws with, so live `d` strings byte-match exports.
export { polyPathLength, polyPathPointAt, roundedPolyPath } from "@archlang/scene/process";
